import React, { useEffect, useState } from 'react';
import { useAdminStore } from '@/store/adminStore';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Search, User, Shield, UserCog, Trash2, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/components/I18nProvider';

const roleConfig = {
  super_admin: { label: 'Super Admin', icon: Shield, color: 'bg-red-100 text-red-700' },
  company_admin: { label: 'Firma Yöneticisi', icon: UserCog, color: 'bg-blue-100 text-blue-700' },
  personnel: { label: 'Personel', icon: User, color: 'bg-gray-100 text-gray-700' },
};

export default function AdminUsersPage() {
  const { t } = useI18n();
  const { users, isLoading, fetchUsers, deleteUser, resetUserPassword } = useAdminStore();
  const [search, setSearch] = useState('');
  const [pwDialog, setPwDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filtered = users.filter((u) =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (u) => {
    if (!window.confirm(`"${u.name}" kullanıcısını silmek istediğinize emin misiniz?`)) return;
    const res = await deleteUser(u.id);
    if (res.success) toast.success('Kullanıcı silindi');
    else toast.error(res.error);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }
    setSaving(true);
    const res = await resetUserPassword(pwDialog.id, newPassword);
    setSaving(false);
    if (res.success) {
      toast.success(`"${pwDialog.name}" için şifre güncellendi`);
      setPwDialog(null);
      setNewPassword('');
    } else {
      toast.error(res.error);
    }
  };

  if (isLoading && users.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="admin-users-page">
        <h1 className="font-heading text-3xl font-bold">Kullanıcı Yönetimi</h1>
        <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-users-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Kullanıcı Yönetimi</h1>
        <p className="mt-1 text-muted-foreground">{users.length} kullanıcı kayıtlı</p>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Kullanıcı ara..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="user-search" />
      </div>

      {/* Mobile Card View */}
      <div className="space-y-3 md:hidden">
        {filtered.map((u) => {
          const rc = roleConfig[u.role] || roleConfig.personnel;
          return (
            <Card key={u.id} data-testid={`user-card-${u.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{u.name}</span>
                      <Badge className={`text-[10px] ${rc.color}`}><rc.icon className="mr-1 h-3 w-3" />{rc.label}</Badge>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground truncate">{u.email}</p>
                    {u.company_name && <p className="text-xs text-muted-foreground">{u.company_name}</p>}
                  </div>
                  <Badge className={u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                    {u.is_active ? 'Aktif' : 'Pasif'}
                  </Badge>
                </div>
                {u.role !== 'super_admin' && (
                  <div className="mt-3 flex gap-2 border-t pt-3">
                    <Button variant="outline" size="sm" onClick={() => { setPwDialog(u); setNewPassword(''); }} data-testid={`pw-btn-${u.id}`}>
                      <KeyRound className="mr-1 h-3.5 w-3.5" />Şifre
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(u)} data-testid={`del-user-${u.id}`}>
                      <Trash2 className="mr-1 h-3.5 w-3.5" />Sil
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && <div className="py-12 text-center text-muted-foreground">Kullanıcı bulunamadı</div>}
      </div>

      {/* Desktop Table View */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-6 py-3 text-left font-medium text-muted-foreground">Ad</th>
                <th className="px-6 py-3 text-left font-medium text-muted-foreground">{t('email')}</th>
                <th className="px-6 py-3 text-left font-medium text-muted-foreground">{t('role')}</th>
                <th className="px-6 py-3 text-left font-medium text-muted-foreground">{t('status')}</th>
                <th className="px-6 py-3 text-left font-medium text-muted-foreground">Kayıt Tarihi</th>
                <th className="px-6 py-3 text-right font-medium text-muted-foreground">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const rc = roleConfig[u.role] || roleConfig.personnel;
                return (
                  <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={`user-row-${u.id}`}>
                    <td className="px-6 py-4 font-medium">{u.name}</td>
                    <td className="px-6 py-4 text-muted-foreground">{u.email}</td>
                    <td className="px-6 py-4"><Badge className={rc.color}><rc.icon className="mr-1 h-3 w-3" />{rc.label}</Badge></td>
                    <td className="px-6 py-4">
                      <Badge className={u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                        {u.is_active ? 'Aktif' : 'Pasif'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">{new Date(u.created_at).toLocaleDateString('tr-TR')}</td>
                    <td className="px-6 py-4 text-right">
                      {u.role !== 'super_admin' && (
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => { setPwDialog(u); setNewPassword(''); }} title="Şifre Sıfırla" data-testid={`pw-btn-${u.id}`}>
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(u)} title="Sil" data-testid={`del-user-${u.id}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">Kullanıcı bulunamadı</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Password Reset Dialog */}
      <Dialog open={!!pwDialog} onOpenChange={() => setPwDialog(null)}>
        <DialogContent data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              Şifre Sıfırla - {pwDialog?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="rounded-lg bg-muted/50 p-3 text-sm">
              <p><span className="font-medium">Kullanıcı:</span> {pwDialog?.name}</p>
              <p><span className="font-medium">E-posta:</span> {pwDialog?.email}</p>
            </div>
            <div className="space-y-1.5">
              <Label>Yeni Şifre</Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="En az 6 karakter..."
                data-testid="new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwDialog(null)}>{t('cancel')}</Button>
            <Button onClick={handleResetPassword} disabled={saving || newPassword.length < 6} data-testid="save-password-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
              Şifreyi Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
